import { Box, Divider, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import KenRadioGroup from '../KenRadioGroup';
import KenLoader from '../KenLoader';
import KenStepperFooter from '../KenApplicationForm/Components/KenStepperFooter/index';
import {
  createUpdateApplication,
  getQuestionsByPaperId,
} from '../../Utils/apiServices';

const useStyles = makeStyles((theme) => ({
  title: {
    color: '#092682',
    fontWeight: 600,
    textAlign: 'left',
    marginBottom: 32,
    fontSize: 20,
  },
  question: {
    fontWeight: 500,
    color: '#091E42',
    marginBottom: 12,
  },
}));

// Normalizes a single question record from the retrieve/questions API into
// a { id, text, options } shape, since the exact backend field names for
// this endpoint aren't documented anywhere in this codebase.
const normalizeQuestion = (q, index) => {
  const id = q?.questionId || q?.recordId || q?.id || `question-${index}`;
  const text = q?.question || q?.questionText || q?.text || '';

  let options = [];
  if (Array.isArray(q?.options)) {
    options = q.options.map((opt) =>
      typeof opt === 'string' ? { label: opt, value: opt } : opt
    );
  } else if (typeof q?.responseOptions === 'string') {
    options = q.responseOptions
      .split(';')
      .filter(Boolean)
      .map((opt) => ({ label: opt, value: opt }));
  } else if (Array.isArray(q?.choices)) {
    options = q.choices.map((opt) =>
      typeof opt === 'string' ? { label: opt, value: opt } : opt
    );
  }

  return { id, text, options };
};

// Rebuilds the { questionId: givenAnswer } map from the assessmentResponses
// array previously saved to context, so answers survive Prev/Next navigation.
const buildAnswersFromResponses = (assessmentResponses) => {
  const answers = {};
  if (Array.isArray(assessmentResponses)) {
    assessmentResponses.forEach((r) => {
      if (r?.assessmentQuestionId) {
        answers[r.assessmentQuestionId] = r.givenAnswer;
      }
    });
  }
  return answers;
};

// Maps each question to the recordId of the response already stored for it, so
// re-submitting this step updates those rows instead of inserting a duplicate
// set. Later entries win, matching how buildAnswersFromResponses resolves the
// displayed answer when a question somehow has more than one stored response.
const buildResponseIdsFromResponses = (assessmentResponses) => {
  const ids = {};
  if (Array.isArray(assessmentResponses)) {
    assessmentResponses.forEach((r) => {
      if (r?.assessmentQuestionId && r?.recordId) {
        ids[r.assessmentQuestionId] = r.recordId;
      }
    });
  }
  return ids;
};

export default function SelectionTest({ applicationState, applicationDispatch }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  // { assessmentQuestionId: recordId } for responses already stored server-side.
  const [responseIds, setResponseIds] = useState({});

  const handlePrev = () => {
    applicationDispatch({ type: 'prevStep' });
  };

  useEffect(() => {
    let cancelled = false;

    const fetchQuestions = async (paperId, existingResponses) => {
      try {
        const res = await getQuestionsByPaperId(paperId);
        if (cancelled) return;
        const rawQuestions =
          res?.data?.questions || res?.data || res?.questions || [];
        setQuestions(
          Array.isArray(rawQuestions)
            ? rawQuestions.map(normalizeQuestion)
            : []
        );
        setAnswers(buildAnswersFromResponses(existingResponses));
        setResponseIds(buildResponseIdsFromResponses(existingResponses));
        setLoading(false);
      } catch (err) {
        console.log('SelectionTest: fetchQuestions err: ', err);
        if (!cancelled) {
          setError('Unable to load the test questions. Please try again later.');
          setLoading(false);
        }
      }
    };

    const existingPaperId =
      applicationState?.applicationData?.application?.assessmentPaperId;

    if (existingPaperId) {
      fetchQuestions(
        existingPaperId,
        // retrieve/application returns assessmentResponses as a top-level
        // sibling of `application`; the nested path is only ever populated by
        // the client-side patch below, which keeps Prev/Next working in-session.
        applicationState?.applicationData?.assessmentResponses ||
          applicationState?.applicationData?.application?.assessmentResponses
      );
      return () => {
        cancelled = true;
      };
    }

    // Fallback: assessmentPaperId isn't in context yet (e.g. resumed session) -
    // re-save the application to get it, same call documentUpload's Next already triggers.
    createUpdateApplication({
      applicationId: applicationState?.applicationData?.application?.recordId,
      applicantId: applicationState?.applicantId,
      application: { ...applicationState?.applicationData?.application },
    })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const paperId = data?.application?.assessmentPaperId;
        if (data) {
          applicationDispatch({
            type: 'update',
            payload: { applicationData: data },
          });
        }
        if (paperId) {
          fetchQuestions(
            paperId,
            data?.assessmentResponses || data?.application?.assessmentResponses
          );
        } else {
          setError('No assessment paper found for this application.');
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log('SelectionTest: createUpdateApplication err: ', err);
        if (!cancelled) {
          setError('Unable to start the selection test. Please try again later.');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (submitting) return;

    const unanswered = questions.some((q) => !answers[q.id]);
    if (unanswered) {
      return enqueueSnackbar('Please answer all questions to continue.', {
        variant: 'info',
      });
    }

    const applicationId = applicationState?.applicationData?.application?.recordId;
    const assessmentResponses = questions.map((q) => {
      const response = {
        applicationId,
        assessmentQuestionId: q.id,
        givenAnswer: answers[q.id],
      };
      // Send the existing row's id so a re-submit updates it. Without this the
      // save inserts a fresh set of responses every time, which duplicates the
      // answers and inflates the server-computed assessmentTotalScore.
      if (responseIds[q.id]) {
        response.recordId = responseIds[q.id];
      }
      return response;
    });
    // Derive the next step from this step's actual position in the stepper,
    // not from the stored counter: revisiting this step used to send
    // storedStep + 1 every time, pushing currentStep past the last index
    // (4, then 5, ...) which left the form blank on reload. Clamp to the
    // last valid index so it can never go out of range again.
    const lastStepIndex = Math.max((applicationState?.totalSteps || 0) - 1, 0);
    const currentStep = Math.min(
      Number(applicationState?.activeStep ?? 0) + 1,
      lastStepIndex
    );

    setSubmitting(true);
    createUpdateApplication({
      applicationId,
      applicantId: applicationState?.applicantId,
      assessmentResponses,
      application: {
        ...applicationState?.applicationData?.application,
        currentStep,
      },
    })
      .then((res) => {
        setSubmitting(false);
        const resData = res?.data || {
          application: { ...applicationState?.applicationData?.application },
        };
        // Refresh the stored row ids from the save response so a second submit
        // in the same session updates the same rows rather than inserting.
        const savedResponses =
          resData?.assessmentResponses || resData?.application?.assessmentResponses;
        if (Array.isArray(savedResponses)) {
          setResponseIds(buildResponseIdsFromResponses(savedResponses));
        }
        if (resData.applicant) {
          resData.applicant.combineFirstLast =
            resData?.applicant?.firstName + ' ' + resData?.applicant?.lastName;
        }
        // Keep the given answers in context even if the backend doesn't echo
        // them back, so returning to this step (Prev/Next) restores them.
        resData.application = {
          ...applicationState?.applicationData?.application,
          ...resData.application,
          currentStep,
          assessmentResponses,
        };
        applicationDispatch({
          type: 'update',
          payload: { applicationData: resData },
        });
        applicationDispatch({ type: 'nextStep' });
      })
      .catch((err) => {
        console.log('SelectionTest: save err: ', err);
        setSubmitting(false);
        enqueueSnackbar('Unable to save your responses. Please try again.', {
          variant: 'error',
        });
      });
  };

  if (loading) {
    return <KenLoader />;
  }

  return (
    <>
      {submitting && <KenLoader />}
      <Box sx={{ width: '50vw', margin: '0 auto', paddingBottom: 10 }}>
        <Typography className={classes.title}>Selection Test</Typography>
        {error ? (
          <Typography color="error">{error}</Typography>
        ) : questions.length === 0 ? (
          <Typography>No questions found for this test.</Typography>
        ) : (
          questions.map((question, index) => (
            <Box key={question.id} mb={4}>
              <Typography className={classes.question}>
                {index + 1}. {question.text}
              </Typography>
              <KenRadioGroup
                row={false}
                options={question.options}
                value={answers[question.id] || null}
                onChange={(event) =>
                  handleAnswerChange(question.id, event.target.value)
                }
              />
              <Divider sx={{ marginTop: 2 }} />
            </Box>
          ))
        )}
      </Box>
      <KenStepperFooter
        totalSteps={applicationState.totalSteps}
        activeStep={applicationState.activeStep}
        onPrev={handlePrev}
        onNext={handleNext}
        isNext={!error && questions.length > 0}
      />
    </>
  );
}
